import { STAGE_META, allItemsForStage, controlDocCode } from "./controlImplementation";
import { formatDdMmYyyy, formatTimeHmsIst } from "./formatDdMmYyyy";

/** @typedef {'file' | 'text'} EvidenceVaultRowKind */

/**
 * @param {unknown} raw
 * @returns {'approved' | 'not_approved'}
 */
export function normalizeReviewStatus(raw) {
  return raw === "approved" ? "approved" : "not_approved";
}

function humanizeItemKey(key) {
  const s = String(key || "").replace(/_/g, " ").trim();
  if (!s) return "-";
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * @param {Record<string, unknown>} profile
 * @param {string} [controlId]
 * @param {string} [stage]
 * @param {string} [itemKey]
 */
function checklistItemLabel(profile, controlId, stage, itemKey) {
  if (!controlId || !stage || !itemKey) return humanizeItemKey(itemKey);
  const pkg = profile?.controlImplementationPackage;
  const doc = pkg?.docs?.[controlId];
  if (!doc || typeof doc !== "object") return humanizeItemKey(itemKey);
  const items = allItemsForStage(doc, stage);
  const hit = items.find((it) => it.key === itemKey);
  if (hit?.label) return hit.label;
  return humanizeItemKey(itemKey);
}

/**
 * @param {Record<string, unknown>} artifact
 * @param {Record<string, unknown>} [profile]
 * @returns {{ sectionName: string, subtabName: string }}
 */
export function artifactSectionSubtab(artifact, profile) {
  const src = artifact?.source;
  if (src === "gap_spoq") {
    const q =
      artifact?.qNo != null && String(artifact.qNo).trim() !== ""
        ? ` · Q${artifact.qNo}`
        : artifact?.questionId != null
          ? ` · ID ${artifact.questionId}`
          : "";
    return { sectionName: "Gap analysis", subtabName: `SPOC${q}` };
  }
  if (src === "gap_delegate") {
    const q =
      artifact?.qNo != null && String(artifact.qNo).trim() !== ""
        ? ` · Q${artifact.qNo}`
        : artifact?.questionId != null
          ? ` · ID ${artifact.questionId}`
          : "";
    return { sectionName: "Gap analysis", subtabName: `Delegate${q}` };
  }
  if (src === "control_implementation") {
    const stage = artifact?.stage;
    const itemKey = artifact?.itemKey;
    const controlId = artifact?.controlId;
    const stageMeta = stage && STAGE_META[stage] ? STAGE_META[stage] : null;
    const stageLabel = stageMeta?.label || humanizeItemKey(stage);
    const itemLabel = checklistItemLabel(profile || {}, String(controlId || ""), String(stage || ""), String(itemKey || ""));
    const code = controlId ? controlDocCode(controlId) : "";
    const sub = code ? `${stageLabel} · ${itemLabel} · ${code}` : `${stageLabel} · ${itemLabel}`;
    return { sectionName: "Control implementation", subtabName: sub };
  }
  const fallback = typeof src === "string" && src ? src.replace(/_/g, " ") : "File upload";
  return { sectionName: fallback, subtabName: "-" };
}

/**
 * @param {string} [name]
 * @returns {{ sectionName: string, subtabName: string }}
 */
export function textEvidenceSectionSubtab(name) {
  const n = String(name || "").trim();
  if (!n) return { sectionName: "Documented evidence", subtabName: "-" };
  const idx = n.indexOf(" - ");
  if (idx === -1) return { sectionName: "Documented evidence", subtabName: n };
  const a = n.slice(0, idx).trim();
  const b = n.slice(idx + 3).trim();
  return { sectionName: a || "Documented evidence", subtabName: b || n };
}

/**
 * @param {Record<string, unknown>} [profile]
 * @returns {Array<{
 *   rowKey: string,
 *   rowKind: EvidenceVaultRowKind,
 *   sectionName: string,
 *   subtabName: string,
 *   sortTs: number,
 *   dateStr: string,
 *   timeStr: string,
 *   reviewStatus: 'approved' | 'not_approved',
 *   fileDisplayName: string,
 *   artifact?: Record<string, unknown>,
 *   evidenceItem?: Record<string, unknown>,
 *   versionCount?: number
 * }>}
 */
export function buildEvidenceVaultRows(profile) {
  /** @type {any[]} */
  const rows = [];
  const artifacts = Array.isArray(profile?.evidenceFileArtifacts) ? profile.evidenceFileArtifacts : [];
  for (const artifact of artifacts) {
    if (!artifact || typeof artifact !== "object") continue;
    const { sectionName, subtabName } = artifactSectionSubtab(artifact, profile);
    const ts = Number(artifact.uploadedAt) || 0;
    const d = ts ? new Date(ts) : null;
    rows.push({
      rowKey: `file:${artifact.id || artifact.url || `i${rows.length}`}`,
      rowKind: "file",
      artifact,
      sectionName,
      subtabName,
      sortTs: ts,
      dateStr: d ? formatDdMmYyyy(ts) : "-",
      timeStr: d ? formatTimeHmsIst(ts) : "-",
      reviewStatus: normalizeReviewStatus(artifact.reviewStatus),
      fileDisplayName: artifact.fileName || "File",
    });
  }
  const ev = Array.isArray(profile?.evidence) ? profile.evidence : [];
  for (const item of ev) {
    if (!item || typeof item !== "object") continue;
    const versions = Array.isArray(item.versions) ? item.versions : [];
    const latest = versions.length ? versions[versions.length - 1] : null;
    const ts = Number(latest?.timestamp) || 0;
    const d = ts ? new Date(ts) : null;
    const { sectionName, subtabName } = textEvidenceSectionSubtab(item.name);
    rows.push({
      rowKey: `text:${item.id}`,
      rowKind: "text",
      evidenceItem: item,
      sectionName,
      subtabName,
      sortTs: ts,
      dateStr: d ? formatDdMmYyyy(ts) : "-",
      timeStr: d ? formatTimeHmsIst(ts) : "-",
      reviewStatus: normalizeReviewStatus(item.reviewStatus),
      fileDisplayName: item.name || "Evidence",
      versionCount: versions.length,
    });
  }
  rows.sort((a, b) => (b.sortTs || 0) - (a.sortTs || 0));
  return rows;
}
